import React, { useState } from "react";
import ReactDOM from "react-dom";
import { useApp } from "../context/AppContext";
import { api, uploadImage } from "../utils/api";
import { t } from "../utils/i18n";

const initials = (name?: string) => (name || "?").split(" ").map(p => p[0]).join("").slice(0, 2).toUpperCase();

// Écran plein écran, sans bouton fermer/passer — bloque toute navigation tant qu'un nouveau
// compte (avatar_required=true) n'a pas posé de photo, voir App.tsx. Réutilise exactement la
// séquence d'upload de Profile.tsx (input file -> POST /uploads/avatar -> PUT /users/me), puis
// lève le blocage côté serveur via POST /users/me/avatar-done (garde-fou avatar_url IS NOT NULL,
// donc appelé seulement après un upload réussi).
export default function MandatoryAvatarStep() {
  const { user, setUser, setToken, lang } = useApp();
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState("");

  const handleFile = async (file: File) => {
    setUploading(true);
    setError("");
    try {
      const url = await uploadImage(file, "avatar");
      if (!url) { setError(t(lang, "onboarding_avatar_upload_failed")); return; }
      setPreviewUrl(url);
      await api.put("/users/me", { avatar_url: url });
      await api.post("/users/me/avatar-done", {});
      setUser({ ...user, avatar_url: url, avatar_required: false });
    } catch {
      setError(t(lang, "onboarding_avatar_connection_error"));
    } finally {
      setUploading(false);
    }
  };

  // Portail direct sur document.body — même bug/fix que DatePromptSheet.tsx : un position:fixed
  // descendant du conteneur mobile de Layout.tsx (WebkitOverflowScrolling:"touch") se positionne
  // sur Safari iOS par rapport au contenu défilant plutôt qu'au vrai viewport.
  return ReactDOM.createPortal(
    <div style={{ position: "fixed", inset: 0, backgroundColor: "white", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, zIndex: 5000 }}>
      <div style={{ width: "100%", maxWidth: 380, textAlign: "center" }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 10 }}>{t(lang, "onboarding_avatar_title")}</h1>
        <p style={{ fontSize: 14.5, color: "#666", marginBottom: 32, lineHeight: 1.5 }}>
          {t(lang, "onboarding_avatar_desc")}
        </p>

        <label style={{ cursor: uploading ? "default" : "pointer", display: "inline-block" }}>
          <input type="file" accept="image/*" disabled={uploading} style={{ display: "none" }} onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
          <div style={{ width: 140, height: 140, borderRadius: "50%", backgroundColor: "#0a0a0a", border: "4px solid #F0F0F0", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto", opacity: uploading ? 0.6 : 1 }}>
            {previewUrl
              ? <img src={previewUrl} alt="avatar" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : <span style={{ color: "white", fontWeight: 800, fontSize: 40 }}>{initials(user?.name)}</span>}
          </div>
          <div style={{ marginTop: 16, fontSize: 13.5, fontWeight: 700, color: "#0a0a0a" }}>
            {uploading ? t(lang, "onboarding_avatar_uploading") : t(lang, "onboarding_avatar_choose")}
          </div>
        </label>

        {!!error && <div style={{ marginTop: 16, fontSize: 13, color: "#c0392b" }}>{error}</div>}

        <button
          onClick={() => setToken(null)}
          style={{ marginTop: 40, background: "none", border: "none", color: "#999", fontSize: 13, cursor: "pointer", textDecoration: "underline" }}
        >
          {t(lang, "logout")}
        </button>
      </div>
    </div>
  ,
    document.body
  );
}
